// src/pages/Login.js
import React, { useState, useContext } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaGoogle } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

// Global styles to set box-sizing
const GlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  background-color: #E3E3E3;
  min-height: calc(100vh - 120px); /* Account for header padding */
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 20px;
`;

const LoginContainer = styled.div`
  width: 75%;
  max-width: 500px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 40px;
  font-family: 'Rubik', sans-serif;

  @media (max-width: 768px) {
    width: 100%;
    padding: 25px;
  }
`;

// Updated Tabs with sliding underline
const Tabs = styled.div`
  position: relative;
  display: flex;
  margin-bottom: 35px;
  background-color: rgba(46, 46, 46, 0.05);
  border-radius: 8px;
  padding: 4px;

  button {
    flex: 1;
    padding: 12px 0;
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    font-family: 'Rubik', sans-serif;
    transition: all 0.3s;
    border-radius: 6px;
    z-index: 1;

    &.active {
      font-weight: 500;
      color: #2e2e2e;
      background-color: white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &:hover:not(.active) {
      color: #555;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;

  input {
    width: 100%;
    padding: 14px 16px;
    border: 2px solid rgba(46, 46, 46, 0.1);
    border-radius: 10px;
    font-size: 16px;
    font-family: 'Rubik', sans-serif;
    transition: all 0.3s;
    background: rgba(46, 46, 46, 0.02);

    &:focus {
      border-color: #2e2e2e;
      outline: none;
      background: white;
    }

    &:focus + label,
    &:not(:placeholder-shown) + label {
      top: -8px;
      left: 10px;
      font-size: 12px;
      color: #2e2e2e;
      background-color: white;
      padding: 0 4px;
    }
  }

  label {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    background-color: transparent;
    color: rgba(46, 46, 46, 0.6);
    font-size: 16px;
    pointer-events: none;
    transition: all 0.2s ease-out;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  margin: 5px 0;

  input {
    margin-right: 10px;
    width: 18px;
    height: 18px;
    accent-color: #2e2e2e;
  }

  label {
    font-size: 14px;
    color: rgba(46, 46, 46, 0.8);
  }

  a {
    color: #2e2e2e;
    text-decoration: underline;
    margin-left: 4px;

    &:hover {
      color: #555;
    }
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background-color: #2e2e2e;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  font-family: 'Rubik', sans-serif;
  transition: all 0.3s;
  margin-top: 10px;

  &:hover {
    background-color: #444;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: rgba(46, 46, 46, 0.4);
    cursor: not-allowed;
    transform: none;
  }
`;

const ComingSoonButton = styled(Button)`
  background-color: #db4437;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
  cursor: not-allowed;

  &:hover {
    background-color: #c33d2e;
  }

  svg {
    margin-right: 8px;
  }
`;

const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 14px;
  margin: 0 0 20px 0;
  padding: 8px 12px;
  background-color: rgba(211, 47, 47, 0.1);
  border-radius: 6px;
  font-family: 'Rubik', sans-serif;
`;

const SuccessMessage = styled.p`
  color: #388e3c;
  font-size: 14px;
  margin: 0 0 20px 0;
  padding: 8px 12px;
  background-color: rgba(56, 142, 60, 0.1);
  border-radius: 6px;
  font-family: 'Rubik', sans-serif;
`;

const Login = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [agreeToTOS, setAgreeToTOS] = useState(false);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { login, signup } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPassword = (password) => {
    return password.length >= 8;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!isValidPassword(password)) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      await login(email, password);
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => navigate(redirect), 1500);
    } catch (err) {
      setError(err.message || 'Failed to login. Please try again.');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!isValidPassword(password)) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!agreeToTOS || !agreeToPrivacy) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    try {
      const result = await signup(email, password, agreeToTOS, agreeToPrivacy);
      if (result.success) {
        setSuccess('Account created successfully! Redirecting to login...');
        setTimeout(() => setActiveTab('login'), 1500);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{activeTab === 'login' ? 'Login | Tempo Stock' : 'Sign Up | Tempo Stock'}</title>
        <meta name="description" content={activeTab === 'login' ? 'Login to your Tempo Stock account' : 'Create a new Tempo Stock account'} />
      </Helmet>
      <GlobalStyle />
      <LoginContainer>
        <Tabs>
          <button
            className={activeTab === 'login' ? 'active' : ''}
            onClick={() => setActiveTab('login')}
          >
            Login
          </button>
          <button
            className={activeTab === 'signup' ? 'active' : ''}
            onClick={() => setActiveTab('signup')}
          >
            Sign Up
          </button>
        </Tabs>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Form onSubmit={activeTab === 'login' ? handleLogin : handleSignup}>
          <InputContainer>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder=" "
              required
            />
            <label htmlFor="email">Email</label>
          </InputContainer>

          <InputContainer>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder=" "
              required
            />
            <label htmlFor="password">Password</label>
          </InputContainer>

          {activeTab === 'signup' && (
            <InputContainer>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder=" "
                required
              />
              <label htmlFor="confirmPassword">Confirm Password</label>
            </InputContainer>
          )}

          {activeTab === 'signup' && (
            <>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="agreeToTOS"
                  checked={agreeToTOS}
                  onChange={(e) => setAgreeToTOS(e.target.checked)}
                  required
                />
                <label htmlFor="agreeToTOS">
                  I agree to the<a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                </label>
              </CheckboxGroup>

              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="agreeToPrivacy"
                  checked={agreeToPrivacy}
                  onChange={(e) => setAgreeToPrivacy(e.target.checked)}
                  required
                />
                <label htmlFor="agreeToPrivacy">
                  I agree to the<a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </label>
              </CheckboxGroup>
            </>
          )}

          {activeTab === 'login' && (
            <CheckboxGroup>
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="rememberMe">
                Remember me
              </label>
            </CheckboxGroup>
          )}

          <Button type="submit">
            {activeTab === 'login' ? 'Login' : 'Create Account'}
          </Button>

          <ComingSoonButton type="button" disabled>
            <FaGoogle /> Continue with Google
          </ComingSoonButton>
        </Form>
      </LoginContainer>
    </PageContainer>
  );
};

export default Login;